import React, { useEffect, useState } from "react";
import { get, post, put, remove } from "../../redux/ApiHelper";
import { errorToast, successToast } from "../../utils/toast.util";
import { QuestionPaperInterface } from "../../interfaces/question-paper.interface";
import { QuestionInterface } from "../../interfaces/question.interface";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
} from "@mui/material";
import { Add, Edit, Delete, Publish, ExpandMore } from "@mui/icons-material";
import TableComponent from "../../components/table/TableComponent";
import { Column } from "../../interfaces/table.interface";
import { QuestionPaperFormDialog, QuestionPaperViewDialog } from "../../components/examination/QuestionPaperDialogs";
import { RootState } from "../../redux/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const Regular = () => {
  const navigate = useNavigate();
  const [questionPapers, setQuestionPapers] = useState<QuestionPaperInterface[]>([]);
  const [filteredPapers, setFilteredPapers] = useState<QuestionPaperInterface[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingPaper, setEditingPaper] = useState<QuestionPaperInterface | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [availableQuestions, setAvailableQuestions] = useState<QuestionInterface[]>([]);
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  // Form state
  const [formData, setFormData] = useState<Partial<QuestionPaperInterface>>({
    title: "",
    description: "",
    subject: "",
    class: "",
    examType: "regular",
    examStartDateTime: "",
    durationInMinutes: 0,
    progressLevel: 1,
    fullMarks: 0,
    passMarks: 0,
    questions: [],
    createNextLevelIfExists: true,
  });

  // Validation errors
  const [errors, setErrors] = useState<{
    title?: string;
    subject?: string;
    class?: string;
    progressLevel?: string;
    examStartDateTime?: string;
    durationInMinutes?: string;
    fullMarks?: string;
    passMarks?: string;
    questions?: string;
  }>({});

  const [publishReviewOpen, setPublishReviewOpen] = useState(false);
  const [publishReviewPaper, setPublishReviewPaper] = useState<QuestionPaperInterface | null>(null);
  const [publishReviewLoading, setPublishReviewLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    fetchQuestionPapers();
    fetchSubjects();
  }, []);

  useEffect(() => {
    setFilteredPapers(questionPapers);
  }, [questionPapers]);

  useEffect(() => {
    if (formData.subject) {
      fetchQuestionsBySubject(formData.subject);
    }
  }, [formData.subject]);

  const fetchQuestionPapers = async () => {
    try {
      setLoading(true);
      const response = await get("/admin/examination/question-papers", {
        examType: "regular",
      });
      setQuestionPapers(response.data || []);
      setFilteredPapers(response.data || []);
    } catch (error) {
      console.error("Error fetching question papers:", error);
      errorToast("Failed to fetch question papers");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await get("/admin/subject/get-all");
      setSubjects(response.subjects || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchQuestionsBySubject = async (subject: string) => {
    try {
      const response = await get("/admin/examination/questions", { subject });
      setAvailableQuestions(response.data || []);
    } catch (error) {
      console.error("Error fetching questions:", error);
    }
  };


  const handleOpenModal = async (paper?: QuestionPaperInterface) => {
    if (paper?._id) {
      try {
        setLoading(true);
        const detailedResp = await get(`/admin/examination/question-paper/${paper._id}`);
        const detailed = (detailedResp?.data || paper) as any;
        setEditingPaper(detailed);
        setFormData({
          title: detailed.title,
          description: detailed.description || "",
          subject: detailed.subject,
          class: detailed.class || "",
          examType: "regular",
          examStartDateTime: detailed.examStartDateTime
            ? new Date(detailed.examStartDateTime).toISOString().slice(0, 16)
            : "",
          durationInMinutes: detailed.durationInMinutes,
          progressLevel: Number(detailed.progressLevel) || 1,
          fullMarks: detailed.fullMarks,
          passMarks: detailed.passMarks,
          // Keep objects when populated so preview works immediately
          questions: Array.isArray(detailed.questions)
            ? detailed.questions.map((q: any) => (typeof q === "string" ? q : q))
            : [],
          createNextLevelIfExists: false,
        });
        setOpenModal(true);
      } catch (error) {
        console.error("Error fetching question paper details:", error);
        errorToast("Failed to fetch question paper details");
      } finally {
        setLoading(false);
      }
      return;
    }

    setEditingPaper(null);
    setFormData({
      title: "",
      description: "",
      subject: "",
      class: "",
      examType: "regular",
      examStartDateTime: "",
      durationInMinutes: 0,
      progressLevel: 1,
      fullMarks: 0,
      passMarks: 0,
      questions: [],
      createNextLevelIfExists: true,
    });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingPaper(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.title || formData.title.trim() === "") {
      newErrors.title = "Title is required";
    }

    if (!formData.subject || formData.subject.trim() === "") {
      newErrors.subject = "Subject is required";
    }

    if (!formData.class || String(formData.class).trim() === "") {
      newErrors.class = "Class is required";
    }

    const lvl = Number((formData as any).progressLevel);
    if (!Number.isFinite(lvl) || !Number.isInteger(lvl) || lvl < 1 || lvl > 100) {
      newErrors.progressLevel = "Progress level must be an integer between 1 and 100";
    }

    if (!formData.durationInMinutes || formData.durationInMinutes <= 0) {
      newErrors.durationInMinutes = "Duration must be greater than 0";
    }

    if (!formData.fullMarks || formData.fullMarks <= 0) {
      newErrors.fullMarks = "Full marks must be greater than 0";
    }

    if (!formData.passMarks || formData.passMarks <= 0) {
      newErrors.passMarks = "Pass marks must be greater than 0";
    } else if (formData.fullMarks && formData.passMarks > formData.fullMarks) {
      newErrors.passMarks = "Pass marks cannot be greater than full marks";
    }

    setErrors(newErrors);
    const messages = Object.values(newErrors).filter(Boolean);
    if (messages.length) {
      errorToast(messages.join(". "));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        questions: (formData.questions || [])
          .map((q: any) => (typeof q === "string" ? q : q?._id))
          .filter(Boolean),
        adminEmail: signedAdmin.email,
        adminRole: signedAdmin.role,
        adminType: signedAdmin.type,
      };

      if (editingPaper?._id) {
        await put(`/admin/examination/question-paper/${editingPaper._id}`, payload);
        successToast("Question paper updated successfully");
        handleCloseModal();
        fetchQuestionPapers();
      } else {
        await post("/admin/examination/question-paper/create", payload);
        successToast("Question paper created successfully");
        handleCloseModal();
        fetchQuestionPapers();
      }
    } catch (error) {
      console.error("Error saving question paper:", error);
      errorToast("Failed to save question paper");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (paperId: string) => {
    if (!window.confirm("Are you sure you want to delete this question paper?")) {
      return;
    }

    try {
      setLoading(true);
      await remove(`/admin/examination/question-paper/${paperId}`);
      successToast("Question paper deleted successfully");
      fetchQuestionPapers();
    } catch (error) {
      console.error("Error deleting question paper:", error);
      errorToast("Failed to delete question paper");
    } finally {
      setLoading(false);
    }
  };

  const openPublishReview = async (paperId: string) => {
    try {
      setPublishReviewLoading(true);
      const resp = await get(`/admin/examination/question-paper/${paperId}`);
      setPublishReviewPaper(resp?.data || null);
      setPublishReviewOpen(true);
    } catch (error) {
      console.error("Error fetching question paper details:", error);
      errorToast("Failed to fetch question paper details");
    } finally {
      setPublishReviewLoading(false);
    }
  };

  const confirmPublish = async () => {
    const paperId = publishReviewPaper?._id;
    if (!paperId) return;

    try {
      setPublishing(true);
      await put(`/admin/examination/question-paper/${paperId}/publish`, {
        adminEmail: signedAdmin.email,
        adminRole: signedAdmin.role,
        adminType: signedAdmin.type,
      });
      successToast("Question paper published successfully");
      setPublishReviewOpen(false);
      setPublishReviewPaper(null);
      fetchQuestionPapers();
    } catch (error) {
      console.error("Error publishing question paper:", error);
      errorToast("Failed to publish question paper");
    } finally {
      setPublishing(false);
    }
  };

  const handleToggleQuestion = (questionId: string) => {
    const currentQuestions = formData.questions || [];
    const questionIds = currentQuestions.map((q: any) => (typeof q === "string" ? q : q._id));
    const newQuestions = questionIds.includes(questionId)
      ? questionIds.filter((id) => id !== questionId)
      : [...questionIds, questionId];
    setFormData({ ...formData, questions: newQuestions });
    if (errors.questions && newQuestions.length > 0) {
      setErrors({ ...errors, questions: undefined });
    }
  };

  const columns: Column[] = [
    { select: "progressLevel", header: "Level", align: "left" },
    { select: "title", header: "Title", align: "left" },
    { select: "durationInMinutes", header: "Duration (min)" },
    { select: "fullMarks", header: "Full Marks" },
    { select: "passMarks", header: "Pass Marks" },
    {
      select: "isPublished",
      header: "Status",
      render: (value: boolean) => (
        <Chip
          label={value ? "Published" : "Draft"}
          color={value ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      select: "actions",
      header: "Actions",
      render: (_: any, row: QuestionPaperInterface) => (
        <Box>
          {!row.isPublished && (<IconButton
            size="small"
            onClick={() => handleOpenModal(row)}
            color="primary"
          >
            <Edit />
          </IconButton>)}
          {!row.isPublished && (
            <IconButton
              size="small"
              onClick={() => openPublishReview(row._id!)}
              color="success"
            >
              <Publish />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => handleDelete(row._id!)}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const grouped = filteredPapers.reduce((acc: Record<string, QuestionPaperInterface[]>, p) => {
    const key = `${p.subject || "—"}__${(p as any).class || "—"}`;
    acc[key] = acc[key] || [];
    acc[key].push(p);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" className="text-white">Regular Exam Papers</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenModal()}
          sx={{
            backgroundColor: "#fcd307",
            color: "#000000",
            "&:hover": {
              backgroundColor: "#e6be06",
            },
          }}
        >
          Create Question Paper
        </Button>
      </Box>

      {groupKeys.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.8)" }}>
            No question papers found.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {groupKeys.map((k) => {
            const [subject, className] = k.split("__");
            const rows = [...grouped[k]].sort(
              (a: any, b: any) => Number(a.progressLevel || 0) - Number(b.progressLevel || 0)
            );
            return (
              <Accordion
                key={k}
                disableGutters
                sx={{
                  bgcolor: "rgba(255,255,255,0.04)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.10)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: "rgba(255,255,255,0.9)" }} />}
                >
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography sx={{ fontWeight: 800, color: "#ffffff" }}>
                      {subject}
                    </Typography>
                    <Chip
                      label={className}
                      size="small"
                      sx={{
                        bgcolor: "rgba(252, 211, 7, 0.14)",
                        color: "#ffffff",
                        border: "1px solid rgba(252, 211, 7, 0.35)",
                      }}
                      variant="outlined"
                    />
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                      {rows.length} level{rows.length === 1 ? "" : "s"}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <TableComponent data={rows} columns={columns} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}

      <QuestionPaperFormDialog
        open={openModal}
        mode={editingPaper ? "edit" : "create"}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        loading={loading}
        questionPaperId={editingPaper?._id}
        signedAdmin={signedAdmin}
        onPaperUpdated={(paper) => {
          if (paper?._id) setEditingPaper(paper);
          fetchQuestionPapers();
        }}
        subjects={subjects}
        availableQuestions={availableQuestions}
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        setErrors={setErrors}
        onToggleQuestion={handleToggleQuestion}
        onGoToQuestionsPage={() => navigate("/examination/questions")}
      />

      <QuestionPaperViewDialog
        open={publishReviewOpen}
        onClose={() => {
          if (publishing) return;
          setPublishReviewOpen(false);
          setPublishReviewPaper(null);
        }}
        selectedPaper={publishReviewPaper}
        primaryActionText={publishReviewLoading ? "Loading..." : publishing ? "Publishing..." : "Publish"}
        primaryActionDisabled={
          publishReviewLoading ||
          publishing ||
          !publishReviewPaper?._id ||
          (((publishReviewPaper as any)?.questions?.length ?? 0) === 0)
        }
        onPrimaryAction={confirmPublish}
      />
    </Box>
  );
};

export default Regular;
